import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "add", "total", "status"]
  static values = { url: String }

  connect() {
    this.requests = new Map()
    this.updateTotal()
  }

  disconnect() {
    for (const request of this.requests.values()) request.abort()
  }

  add() {
    const row = this.rowTargets.find(row => row.hidden)
    if (!row) return
    row.hidden = false
    row.disabled = false
    row.querySelector('[data-part="kind"]').focus()
    this.updateTotal()
  }

  remove(event) {
    const row = event.target.closest("fieldset")
    this.clear(row)
    row.querySelector('[data-part="percentage"]').value = ""
    row.hidden = true
    row.disabled = true
    this.updateTotal()
    this.addTarget.focus()
  }

  mode(event) {
    const row = event.target.closest("fieldset")
    this.clear(row)
    const external = event.target.value === "external"
    row.querySelector('[data-part="search"]').hidden = external
    row.querySelector('[data-part="external"]').hidden = !external
  }

  clear(row) {
    this.requests.get(row)?.abort()
    this.requests.delete(row)
    for (const part of ["reference", "query", "destination"]) row.querySelector(`[data-part="${part}"]`).value = ""
    row.querySelector('[data-part="selected"]').textContent = "No recipient selected."
    row.querySelector('[data-part="results"]').replaceChildren()
  }

  edited(event) {
    const row = event.target.closest("fieldset")
    this.requests.get(row)?.abort()
    this.requests.delete(row)
    row.querySelector('[data-part="reference"]').value = ""
    row.querySelector('[data-part="selected"]').textContent = "Search and select a recipient."
    row.querySelector('[data-part="results"]').replaceChildren()
  }

  async search(event) {
    event.preventDefault()
    const row = event.target.closest("fieldset")
    const query = row.querySelector('[data-part="query"]').value.trim()
    const kind = row.querySelector('[data-part="kind"]').value
    const results = row.querySelector('[data-part="results"]')
    const status = row.querySelector('[data-part="selected"]')
    this.requests.get(row)?.abort()
    results.replaceChildren()
    if (query.length < 2) { status.textContent = "Enter at least two letters."; return }
    const controller = new AbortController()
    this.requests.set(row, controller)
    status.textContent = "Finding recipients…"
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("kind", kind)
      url.searchParams.set("q", query)
      const response = await fetch(url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal })
      const data = await response.json()
      if (this.requests.get(row) !== controller || row.hidden) return
      if (!response.ok) throw new Error(data.error || "Search is temporarily unavailable.")
      status.textContent = data.recipients.length ? "Select the exact recipient and check its MAZA address." : "No eligible recipients found. Try another name."
      for (const recipient of data.recipients) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "tc-split-result"
        // Never interpret names, titles, or addresses as HTML.
        button.textContent = `${recipient.label} · ${recipient.destination}`
        button.addEventListener("click", () => {
          row.querySelector('[data-part="reference"]').value = recipient.reference_id
          status.textContent = `${recipient.label} → ${recipient.destination}`
          results.replaceChildren()
          row.querySelector('[data-part="percentage"]').focus()
        })
        results.append(button)
      }
    } catch (error) {
      if (error.name !== "AbortError" && this.requests.get(row) === controller) status.textContent = error.message || "Search is temporarily unavailable."
    }
  }

  updateTotal() {
    if (!this.hasTotalTarget) return
    const active = this.rowTargets.filter(row => !row.hidden)
    const total = active.reduce((sum, row) => {
      const raw = row.querySelector('[data-part="percentage"]').value
      if (!/^\d{1,11}(?:\.\d{1,8})?$/.test(raw)) return sum
      const [whole, fraction = ""] = raw.split(".")
      return sum + BigInt(whole) * 100000000n + BigInt(fraction.padEnd(8, "0"))
    }, 0n)
    const fraction = (total % 100000000n).toString().padStart(8, "0").replace(/0+$/, "")
    const amount = (total / 100000000n).toLocaleString() + (fraction ? `.${fraction}` : "")
    this.totalTarget.textContent = `${amount} MAZA designated across ${active.length} split${active.length === 1 ? "" : "s"}`
    this.statusTarget.textContent = "If your paycheck cannot cover every split, all splits are skipped and the full amount goes to you. Final amounts are checked exactly on review."
    this.addTarget.disabled = active.length >= 9
  }

  back() { window.history.back() }
}
